/**
 * This file takes care of global app side effects,
 * like clicking on add to cart and the cart modal being displayed
 */

import { useEffect, useState } from "react"
import { useStore } from "@tanstack/react-store"
import { effect, signal } from "~/sdk/signal"

const displayCart = signal(false)
const loadedOnMouseMove = signal(false)

effect(() => {
    const loadOnMouseMove = () => loadedOnMouseMove.value = true

    // The framework's `effect` shim defers to a microtask and this module is
    // imported during hydration, which frequently runs AFTER the window `load`
    // event has already fired. A bare `load` listener then never fires and the
    // signal stays false forever, so every consumer gated on it (floating
    // buttons / back-to-top, cart badge, popups) never renders. Mirror the
    // original site's TimerJS guard: if the page is already loaded, flip now.
    if (document.readyState === 'complete') {
        loadOnMouseMove()
        return
    }

    self.addEventListener('load', loadOnMouseMove, { once: true })

    return () => {
        self.removeEventListener('load', loadOnMouseMove)
    }
})

const state = {
    displayCart,
    loadedOnMouseMove,
}

export const useUI = () => state

// Reactive hooks. The underlying signals are backed by @tanstack/store and
// reading `.value` inside a render body does NOT subscribe to updates — so
// `displayCart.value = true` from AddToCartButton never triggered the
// minicart drawer to open. Use these hooks in render bodies; keep writing
// via `displayCart.value = true/false` in event handlers.
//
// Cast `as any` because Store implements BaseAtom's runtime contract
// (`.get`, `.subscribe`) but the type system doesn't accept Store as
// BaseAtom in @tanstack/react-store@0.9.
export function useDisplayCart(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return useStore(displayCart.store as any, (s: boolean) => s)
}

export function useLoadedOnMouseMove(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loaded = useStore(loadedOnMouseMove.store as any, (s: boolean) => s)

    // The `effect` shim above flips `loadedOnMouseMove` to true in a microtask
    // during hydration (when `document.readyState === 'complete'`). That makes
    // every consumer gated on this hook (cart badge, floating buttons, popups)
    // render content on the client while the server rendered nothing → React
    // hydration mismatch (DECO-5454). Gate on a post-mount flag so the FIRST
    // client render always matches the server (both false); only after
    // hydration completes do we reflect the real signal.
    const [mounted, setMounted] = useState(false)
    useEffect(() => setMounted(true), [])

    return mounted && loaded
}
